function toOriginOnly(raw: string): string {
    const trimmed = raw.trim()
    if (!trimmed) {
        return ''
    }

    try {
        return new URL(trimmed).origin
    } catch {
        return trimmed.split(/[?#]/)[0].replace(/\/$/, '')
    }
}

/**
 * Share/QR base URL for the deployment currently loaded in the browser.
 * Resolved at runtime so stage → prod sync does not need to rewrite bundles.
 * Always returns the bare origin (scheme + host), without path, query, or hash.
 */
export function getGameShareUrl(): string {
    if (typeof window !== 'undefined' && window.location?.href) {
        return toOriginOnly(window.location.href)
    }

    return toOriginOnly(String(import.meta.env.PAGE_URL || ''))
}

/** @deprecated Prefer `getGameShareUrl()` when the URL may be read after navigation. */
export const GAME_SHARE_URL = getGameShareUrl()

/**
 * Copies a URL to the clipboard (default: `GAME_SHARE_URL`).
 *
 * @param shareUrl Full URL to copy.
 */
export async function copyGameUrlToClipboard(
    shareUrl: string = getGameShareUrl(),
): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(shareUrl)
        return true
    } catch {
        try {
            const textarea = document.createElement('textarea')
            textarea.value = shareUrl
            textarea.style.position = 'fixed'
            textarea.style.left = '-9999px'
            document.body.appendChild(textarea)
            textarea.select()
            const ok = document.execCommand('copy')
            document.body.removeChild(textarea)
            return ok
        } catch {
            return false
        }
    }
}

export const GAME_SHARE_HEADLINE = 'LifeRush'
export const GAME_SHARE_SUBLINE_PARENT =
    'Ich habe bei LIFE RUSH eine coole Sammelkarte erspielt 😎Die gibt’s auch als echte Karte! Können wir sie zusammen in einer Sparkasse abholen?'
export const GAME_SHARE_SUBLINE_FRIEND =
    'Ich hab bei LIFE RUSH das Leben durchgespielt und eine seltene Sammelkarte erspielt.😎 Schaffst du die auch?'
export const GAME_SHARE_SUBLINE_SETTINGS =
    'LIFE RUSH – Ready? Set. Grow! Spiel dein Leben durch und gewinn eine limitierte Sammelkarte! Jetzt spielen 🎮'

export type GameShareAudience = 'parent' | 'friend' | 'settings'

const GAME_SHARE_SUBLINE: Record<GameShareAudience, string> = {
    parent: GAME_SHARE_SUBLINE_PARENT,
    friend: GAME_SHARE_SUBLINE_FRIEND,
    settings: GAME_SHARE_SUBLINE_SETTINGS,
}

/**
 * Opens the system share sheet when available; otherwise copies text + URL to the
 * clipboard. Share sheet dismiss (incl. cancel) is treated as completion.
 *
 * @param shareUrl Full URL to share; defaults to `GAME_SHARE_URL`.
 * @param audience Share text variant for parents or friends.
 */
export async function shareGameLink(
    shareUrl: string = getGameShareUrl(),
    audience: GameShareAudience = 'parent',
): Promise<boolean> {
    const shareText = GAME_SHARE_SUBLINE[audience]
    const payload = {
        title: GAME_SHARE_HEADLINE,
        text: shareText,
        url: shareUrl,
    }

    if (typeof navigator !== 'undefined' && navigator.share) {
        const canShare =
            typeof navigator.canShare === 'function'
                ? navigator.canShare(payload)
                : true
        if (canShare) {
            try {
                await navigator.share(payload)
            } catch {
                // User dismissed the sheet or share failed.
            }
            return true
        }
    }

    const clipboardText =
        audience === 'settings'
            ? `${shareText}\n${shareUrl}`
            : `${GAME_SHARE_HEADLINE}\n${shareText}\n${shareUrl}`

    try {
        await navigator.clipboard.writeText(clipboardText)
        return true
    } catch {
        return false
    }
}
